import { Colors, Lightning, Router, Storage } from "@lightningjs/sdk";
import { TranslatableText } from "../../components/data-display/TranslatableText/TranslatableText";
import { Button } from "../../components/input/Button/Button";
import theme from "../../lib/theme";
import { STORAGE_KEYS } from "../../lib/utils";

const AGE_OPTIONS = [0, 6, 12, 16, 18];
const DEFAULT_AGE_INDEX = 2; // 12+
const AGE_BOX_W = 80;
const AGE_BOX_H = 56;
const AGE_BOX_SPACING = 14;
const SUCCESS_AUTOCLOSE_MS = 1500;

interface AgeBoxTemplateSpec extends Lightning.Component.TemplateSpec {
    Background: object;
    Label: object;
}

class AgeBox
    extends Lightning.Component<AgeBoxTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<AgeBoxTemplateSpec>
{
    private _age: number = 0;
    private _selected: boolean = false;

    static override _template(): Lightning.Component.Template<AgeBoxTemplateSpec> {
        return {
            w: AGE_BOX_W,
            h: AGE_BOX_H,
            Background: {
                w: AGE_BOX_W,
                h: AGE_BOX_H,
                // Texture-based rounded rect — RoundedRectangleShader2 not implemented on STB Chromium 55.
                texture: Lightning.Tools.getRoundRect(
                    AGE_BOX_W,
                    AGE_BOX_H,
                    10,
                    2,
                    Colors(theme.colors.text.primary).get(),
                    false,
                    0x00000000
                )
            },
            Label: {
                x: AGE_BOX_W / 2,
                y: AGE_BOX_H / 2,
                mount: 0.5,
                text: {
                    text: "",
                    fontSize: 24,
                    fontFace: "SemiBold",
                    textColor: Colors(theme.colors.text.primary).get(),
                    textAlign: "center"
                }
            }
        };
    }

    setAge(age: number) {
        this._age = age;
        (this.getByRef("Label") as any).patch({ text: { text: `${age}+` } });
    }

    setSelected(selected: boolean) {
        this._selected = selected;
        const bg = this.getByRef("Background") as any;
        const label = this.getByRef("Label") as any;
        if (selected) {
            bg.patch({
                texture: Lightning.Tools.getRoundRect(
                    AGE_BOX_W,
                    AGE_BOX_H,
                    10,
                    2,
                    Colors(theme.colors.text.primary).get(),
                    true,
                    Colors(theme.colors.text.primary).get()
                )
            });
            label.patch({ text: { textColor: Colors(theme.colors.bg.secondary).get() } });
        } else {
            bg.patch({
                texture: Lightning.Tools.getRoundRect(
                    AGE_BOX_W,
                    AGE_BOX_H,
                    10,
                    2,
                    Colors(theme.colors.text.primary).get(),
                    false,
                    0x00000000
                )
            });
            label.patch({ text: { textColor: Colors(theme.colors.text.primary).get() } });
        }
    }
}

interface AgeSelectorTemplateSpec extends Lightning.Component.TemplateSpec {
    Box: {
        Background: object;
        Content: {
            Title: typeof TranslatableText;
            Description: typeof TranslatableText;
            AgeRow: object;
            Actions: {
                OkBtn: typeof Button;
            };
            SuccessView: {
                Message: typeof TranslatableText;
            };
        };
    };
}

export class AgeSelector
    extends Lightning.Component<AgeSelectorTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<AgeSelectorTemplateSpec>
{
    private _selectedIndex: number = DEFAULT_AGE_INDEX;
    private _onConfirm?: (age: string) => void;
    private _onCancel?: () => void;
    private _autocloseTimeout: NodeJS.Timeout | null = null;

    readonly Box = this.getByRef("Box")!;
    readonly Content = this.Box.getByRef("Content")!;
    readonly Title = this.Content.getByRef("Title")!;
    readonly Description = this.Content.getByRef("Description")!;
    readonly AgeRow = this.Content.getByRef("AgeRow")!;
    readonly Actions = this.Content.getByRef("Actions")!;
    readonly OkBtn = this.Actions.getByRef("OkBtn")!;
    readonly SuccessView = this.Content.getByRef("SuccessView")!;

    static override _template(): Lightning.Component.Template<AgeSelectorTemplateSpec> {
        const rowWidth = AGE_BOX_W * AGE_OPTIONS.length + AGE_BOX_SPACING * (AGE_OPTIONS.length - 1);
        const boxW = Math.max(rowWidth + 80, 628);
        const boxH = 440;
        const descWrapW = boxW - 96;

        const ageBoxes: Record<string, any> = {};
        AGE_OPTIONS.forEach((age, i) => {
            ageBoxes[`Box${i}`] = {
                type: AgeBox,
                flexItem: i < AGE_OPTIONS.length - 1 ? { marginRight: AGE_BOX_SPACING } : {}
            };
        });

        return {
            zIndex: 50,
            w: theme.layout.w,
            h: theme.layout.h,
            x: 0,
            y: 0,
            rect: true,
            color: Colors(theme.colors.bg.overlay).alpha(0.7).get(),
            alpha: 0,
            visible: false,
            collision: true,
            Box: {
                w: boxW,
                h: boxH,
                mount: 0.5,
                x: (w) => w / 2,
                y: (h) => h / 2,
                alpha: 0,
                Background: {
                    x: -1,
                    y: -1,
                    texture: Lightning.Tools.getRoundRect(
                        boxW,
                        boxH,
                        12,
                        0,
                        undefined,
                        true,
                        Colors(theme.colors.bg.secondary).get()
                    )
                },
                Content: {
                    w: (w) => w,
                    h: (h) => h,
                    flex: {
                        direction: "column",
                        justifyContent: "center",
                        alignItems: "center"
                    },
                    Title: {
                        flexItem: { marginBottom: 16 },
                        type: TranslatableText,
                        key: "settings.ageRestriction",
                        text: {
                            fontSize: 28,
                            fontFace: "SemiBold",
                            textColor: Colors(theme.colors.text.primary).get(),
                            textAlign: "center"
                        }
                    },
                    Description: {
                        flexItem: { marginBottom: 32 },
                        type: TranslatableText,
                        key: "settings.ageRestrictionHint",
                        // Apps/languages without the key render nothing instead of the raw key.
                        skipEmpty: true,
                        text: {
                            fontSize: 22,
                            fontFace: "Regular",
                            lineHeight: 30,
                            textColor: Colors(theme.colors.text.primary).get(),
                            textAlign: "center",
                            wordWrap: true,
                            wordWrapWidth: descWrapW
                        }
                    },
                    AgeRow: {
                        flexItem: { marginBottom: 40 },
                        w: rowWidth,
                        h: AGE_BOX_H,
                        flex: { direction: "row", alignItems: "center" },
                        ...ageBoxes
                    },
                    Actions: {
                        w: 300,
                        h: theme.button.h || 56,
                        OkBtn: {
                            type: Button,
                            w: 300,
                            label: "OK",
                            actionId: "ageOk",
                            signals: {
                                handleClick: "_okClick"
                            }
                        }
                    },
                    SuccessView: {
                        // SuccessView replaces the main view content on confirm — explicit
                        // positioning instead of participating in the column flex flow.
                        mountX: 0.5,
                        x: (w: number) => w / 2,
                        y: 80,
                        alpha: 0,
                        visible: false,
                        w: (w) => w,
                        flex: {
                            direction: "column",
                            alignItems: "center",
                            justifyContent: "center"
                        },
                        Message: {
                            type: TranslatableText,
                            key: "pin.ageChangeSuccess",
                            text: {
                                fontSize: 24,
                                fontFace: "SemiBold",
                                textColor: Colors(theme.colors.text.primary).get(),
                                textAlign: "center"
                            }
                        }
                    }
                }
            }
        };
    }

    // State machine replaced with a single _focusArea field — Lightning state classes
    // sometimes swallow keys when _getFocused returns `this` (the widget itself).
    private _focusArea: "row" | "ok" = "row";

    override _getFocused(): any {
        return this._focusArea === "ok" ? (this.OkBtn as any) : this;
    }

    override _handleLeft() {
        if (this._focusArea !== "row") return false;
        console.info("[JULIA-AgeSelector] left", this._selectedIndex);
        if (this._selectedIndex > 0) {
            this._selectedIndex--;
            this._updateSelection();
        }
        return true;
    }

    override _handleRight() {
        if (this._focusArea !== "row") return false;
        console.info("[JULIA-AgeSelector] right", this._selectedIndex);
        if (this._selectedIndex < AGE_OPTIONS.length - 1) {
            this._selectedIndex++;
            this._updateSelection();
        }
        return true;
    }

    override _handleDown() {
        if (this._focusArea !== "row") return false;
        console.info("[JULIA-AgeSelector] down → ok");
        this._focusArea = "ok";
        this._refocus();
        return true;
    }

    override _handleUp() {
        if (this._focusArea !== "ok") return true;
        console.info("[JULIA-AgeSelector] up → row");
        this._focusArea = "row";
        this._refocus();
        return true;
    }

    override _handleEnter() {
        console.info("[JULIA-AgeSelector] enter", this._focusArea);
        if (this._focusArea === "row") {
            this._focusArea = "ok";
            this._refocus();
        } else {
            this._confirm();
        }
        return true;
    }

    override _handleBack() {
        console.info("[JULIA-AgeSelector] back", this._focusArea);
        if (this._focusArea === "ok") {
            this._focusArea = "row";
            this._refocus();
        } else {
            this._cancel();
        }
        return true;
    }

    _okClick() {
        console.info("[JULIA-AgeSelector] ok click signal");
        this._confirm();
    }

    $handleBtnClick(id: string) {
        console.info("[JULIA-AgeSelector] $handleBtnClick", id);
        if (id === "ageOk") {
            this._confirm();
        }
    }

    open(onConfirm: (age: string) => void, currentAge?: string, onCancel?: () => void) {
        console.info("[JULIA-AgeSelector] open() called", { currentAge });
        this._onConfirm = onConfirm;
        this._onCancel = onCancel;
        this._selectedIndex = this._resolveInitialIndex(currentAge);

        let foundBoxes = 0;
        AGE_OPTIONS.forEach((age, i) => {
            const box = this.AgeRow.getByRef(`Box${i}`) as AgeBox;
            if (box) {
                box.setAge(age);
                foundBoxes++;
            }
        });
        console.info("[JULIA-AgeSelector] boxes found:", foundBoxes, "/", AGE_OPTIONS.length);

        this._hideSuccess();
        this._updateSelection();
        this._focusArea = "row";

        this.patch({ alpha: 1, visible: true });
        this.Box.patch({ alpha: 1, visible: true });

        Router.focusWidget("AgeSelector");
        console.info("[JULIA-AgeSelector] Router.focusWidget called");

        // PinInput.close() (running synchronously AFTER our _onPinSet callback)
        // calls Router.focusPage() which overrides the focusWidget above —
        // keys then bubble to the still-mounted PinInput (mode reset to "set").
        // Reassert focus on the next tick when PinInput's close() has finished.
        setTimeout(() => {
            Router.focusWidget("AgeSelector");
            this._focus();
            this._refocus();
            console.info("[JULIA-AgeSelector] post-timeout: re-focused, _focusArea=", this._focusArea);
        }, 150);
    }

    override _focus() {
        console.info("[JULIA-AgeSelector] _focus fired");
    }

    close() {
        if (this._autocloseTimeout) {
            clearTimeout(this._autocloseTimeout);
            this._autocloseTimeout = null;
        }
        this._onConfirm = undefined;
        this._onCancel = undefined;
        this._selectedIndex = DEFAULT_AGE_INDEX;
        this.patch({ alpha: 0, visible: false });
        this.Box.patch({ alpha: 0, visible: false });
        Router.focusPage();
    }

    private _resolveInitialIndex(currentAge?: string): number {
        const raw =
            currentAge ??
            (() => {
                const user = Storage.get(STORAGE_KEYS.user) as any;
                return (
                    user?.settings?.parentalAgeRestriction ||
                    (Storage.get(STORAGE_KEYS.parentalAgeRestriction) as string | undefined)
                );
            })();
        if (!raw) return DEFAULT_AGE_INDEX;
        const ageNum = parseInt(raw, 10);
        if (isNaN(ageNum)) return DEFAULT_AGE_INDEX;
        const idx = AGE_OPTIONS.indexOf(ageNum);
        return idx >= 0 ? idx : DEFAULT_AGE_INDEX;
    }

    private _updateSelection() {
        console.info("[JULIA-AgeSelector] _updateSelection idx=", this._selectedIndex);
        AGE_OPTIONS.forEach((_, i) => {
            const box = this.AgeRow.getByRef(`Box${i}`) as AgeBox;
            if (box) box.setSelected(i === this._selectedIndex);
        });
    }

    private _confirm() {
        const age = AGE_OPTIONS[this._selectedIndex]!;
        const ageStr = age < 10 ? `0${age}` : `${age}`;
        this._onConfirm?.(ageStr);
        this._showSuccess();
        this._autocloseTimeout = setTimeout(() => this.close(), SUCCESS_AUTOCLOSE_MS);
    }

    private _cancel() {
        const cb = this._onCancel;
        this.close();
        cb?.();
    }

    private _showSuccess() {
        this.Title.patch({ alpha: 0, visible: false });
        this.Description.patch({ alpha: 0, visible: false });
        this.AgeRow.patch({ alpha: 0, visible: false });
        this.Actions.patch({ alpha: 0, visible: false });
        this.SuccessView.patch({ alpha: 1, visible: true });
    }

    private _hideSuccess() {
        this.Title.patch({ alpha: 1, visible: true });
        this.Description.patch({ alpha: 1, visible: true });
        this.AgeRow.patch({ alpha: 1, visible: true });
        this.Actions.patch({ alpha: 1, visible: true });
        this.SuccessView.patch({ alpha: 0, visible: false });
    }
}
